import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Building2 } from 'lucide-react';
import { api } from '../../services/api';
import Dialog from '../ui/Dialog';
import Field, { FormErrorSummary, focusField, firstErrorKey, hasErrors } from '../ui/Field';
import StatusCircles from '../StatusCircles';
import { ServerError } from './formBits';
import {
  MILESTONE_FIELD_ORDER, MILESTONE_PLANNED_MODES, buildMilestoneBody, emptyMilestoneValues, milestoneValuesOf,
  parentLocked, parentOptions, validateMilestoneForm, type MilestoneFormValues,
} from './milestoneFormModel';
import type { Milestone } from '../../types';
import HideFromCustomerToggle from '../HideFromCustomerToggle';
import EntityPicker from '../ui/EntityPicker';
import DateInput from '../ui/DateInput';
import { AdditionalAssigneesCombobox } from '../tasks/TaskAssigneesField';
import { useInternalOrgId } from '../../hooks/useInternalOrg';
import { CUSTOMERS_GROUP_KEY, INTERNAL_WORK_LABEL_KEY, internalFirstPickerOptions } from '../../utils/internalWorkOption';

/**
 * P-05/C (D-52) — "אבן דרך חדשה" / "עריכת אבן דרך", ONE form everywhere:
 *
 *   אבן דרך חדשה — <לקוח> › <פרויקט>
 *   שם * · תיאור · אחראי (לא חובה) · מצוותים · התחלה · יעד · סטטוס (עיגולים)
 *   שעות מוערכות: לא מוגדר / אוטומטי / ידני [__] · תחת אבן דרך [— ראשית —]
 *   [ביטול] [שמירה] [שמירה והוספת משימות לאבן דרך זו]
 *
 * Opened from the project page (customer + project known), from the
 * milestone page ("+ תת-אבן דרך": the parent pre-set), and from /milestones
 * (customer → project picked first — only projects / developments of that
 * customer). "תחת אבן דרך" lists the top-level milestones of the same
 * project; it is locked on a milestone with sub-milestones (and on a core
 * one). A core milestone keeps its name (D-22).
 *
 * `onDone(milestone, addTasks)` — `addTasks` = the "שמירה והוספת משימות"
 * button: the caller opens the task form locked on that milestone.
 */
interface EditableMilestone extends Partial<Milestone> {
  id: string;
  project_id: string | null;
  organization_id?: string;
  organization_name?: string;
  project_name?: string;
  milestone_assignees?: { user_id: string }[];
}

export type MilestoneFormModalProps = {
  onClose: () => void;
  onDone: (milestone: Milestone, addTasks: boolean) => void;
} & (
  | {
      /** Project page / milestone page: a milestone of this project (`parentId` = "+ תת-אבן דרך"). */
      variant: 'create';
      projectId: string;
      organizationId: string;
      customerName?: string;
      projectName?: string;
      parentId?: string;
    }
  | {
      /** /milestones: customer → project picked in the form. */
      variant: 'list-create';
    }
  | {
      variant: 'edit';
      milestone: EditableMilestone;
      organizationId: string;
      customerName?: string;
      projectName?: string;
    }
);

const FORM_ID = 'milestone-form';

export default function MilestoneFormModal(props: MilestoneFormModalProps) {
  const { t } = useTranslation();
  const { variant, onClose, onDone } = props;
  const isEdit = variant === 'edit';
  const pickProject = variant === 'list-create';
  const self = isEdit ? props.milestone : undefined;
  const coreName = !!self?.is_core;

  const [values, setValues] = useState<MilestoneFormValues>(() => {
    if (props.variant === 'edit') return milestoneValuesOf(props.milestone as any);
    const v = emptyMilestoneValues();
    if (props.variant === 'create') { v.projectId = props.projectId; v.parentId = props.parentId ?? ''; }
    return v;
  });
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState<null | 'save' | 'tasks'>(null);
  const [serverError, setServerError] = useState<unknown>(null);

  // /milestones — the customer, then its projects / developments.
  const { data: customersRes } = useQuery({
    queryKey: ['organizations', 'active-picker', 'with-internal'],
    // D-274 §1 — the milestones of a project of "עבודה פנימית OTE" too.
    queryFn: () => api.getOrganizations({ status: 'active', limit: 500, include_internal: 1 }),
    staleTime: 60_000,
    enabled: pickProject,
  });
  const customers = (customersRes?.data ?? []) as { id: string; name: string; is_internal?: boolean | null }[];
  // D-274 §1 — "עבודה פנימית OTE" first, apart from the customers.
  const internalOrgId = useInternalOrgId();
  const { data: projectsRes, isLoading: projectsLoading } = useQuery({
    queryKey: ['projects-of-customer', values.customerId],
    queryFn: () => api.getProjects({ organization_id: values.customerId, limit: 200 }),
    staleTime: 60_000,
    enabled: pickProject && !!values.customerId,
  });
  const projects = (projectsRes?.data ?? []) as { id: string; name: string; kind?: string; type?: string }[];

  const projectId = values.projectId;
  const orgId = props.variant === 'list-create' ? values.customerId : props.organizationId;
  const customerName = props.variant === 'list-create' ? customers.find((c) => c.id === values.customerId)?.name : props.customerName;
  const projectName = props.variant === 'list-create' ? projects.find((p) => p.id === values.projectId)?.name : props.projectName;

  // "תחת אבן דרך": the project's milestones.
  const { data: msRes } = useQuery({
    queryKey: ['milestones', projectId],
    queryFn: () => api.getMilestones(projectId),
    staleTime: 30_000,
    enabled: !!projectId,
  });
  const projectMilestones = useMemo(() => (msRes?.data ?? []) as Milestone[], [msRes]);
  const parents = useMemo(() => parentOptions(projectMilestones, self?.id), [projectMilestones, self?.id]);
  const lockParent = parentLocked(self?.id, coreName, projectMilestones);

  const { data: agentsRes, isLoading: agentsLoading } = useQuery({
    queryKey: ['assignable-agents', orgId],
    queryFn: () => api.getAssignableAgents(orgId!),
    staleTime: 60_000,
    enabled: !!orgId,
  });
  const agents = agentsRes?.data ?? [];

  const set = <K extends keyof MilestoneFormValues>(key: K, value: MilestoneFormValues[K]) => setValues((v) => ({ ...v, [key]: value }));
  const check = (next: MilestoneFormValues) => validateMilestoneForm(next, { pickProject }, t);
  const onBlurField = (id: string) => setErrors((prev) => ({ ...prev, [id]: check(values)[id] }));
  const change = <K extends keyof MilestoneFormValues>(key: K, value: MilestoneFormValues[K], id: string) => {
    const next = { ...values, [key]: value };
    setValues(next);
    if (errors[id]) setErrors((prev) => ({ ...prev, [id]: check(next)[id] }));
  };

  const send = async () => {
    switch (props.variant) {
      case 'create':
        return api.createMilestone(props.projectId, buildMilestoneBody(values));
      case 'list-create':
        return api.createParentedMilestone(buildMilestoneBody(values, { withProjectId: true }));
      case 'edit': {
        const body: Record<string, unknown> = buildMilestoneBody(values);
        // A core milestone's name is fixed; the parent is not sent while locked.
        if (lockParent) delete body.parent_milestone_id;
        return api.updateMilestone(props.milestone.id, body as any);
      }
    }
  };

  const submit = async (addTasks: boolean) => {
    setServerError(null);
    const next = check(values);
    setErrors(next);
    setSubmitted(true);
    if (hasErrors(next)) {
      const first = firstErrorKey(next, MILESTONE_FIELD_ORDER);
      if (first) focusField(first);
      return;
    }
    setSaving(addTasks ? 'tasks' : 'save');
    try {
      const res = await send();
      onDone((res?.data ?? { id: self?.id }) as Milestone, addTasks);
    } catch (err: unknown) {
      setServerError(err);
    } finally {
      setSaving(null);
    }
  };

  const handleSubmit = (e: FormEvent) => { e.preventDefault(); void submit(false); };

  const labels: Record<string, string> = {
    'ms-customer': t('milestoneForm.customer'),
    'ms-project': t('milestoneForm.project'),
    'ms-name': t('milestoneForm.name'),
    'ms-due': t('milestoneForm.dueDate'),
    'ms-planned': t('milestoneForm.plannedManual'),
  };
  const path = [customerName, projectName].filter(Boolean).join(' › ');
  const title = `${isEdit ? t('milestoneForm.titleEdit') : t('milestoneForm.titleNew')}${path ? ` — ${path}` : ''}`;
  const busy = saving !== null;

  return (
    <Dialog
      open
      onClose={onClose}
      title={title}
      size="lg"
      dismissible={!busy}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>{t('common.cancel')}</button>
          <button type="submit" form={FORM_ID} className="btn btn-secondary" disabled={busy} aria-busy={saving === 'save' || undefined}>
            {saving === 'save' && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
            {t('milestoneForm.save')}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void submit(true)} disabled={busy} aria-busy={saving === 'tasks' || undefined}>
            {saving === 'tasks' && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
            {t('milestoneForm.saveAndAddTasks')}
          </button>
        </>
      )}
    >
      <form id={FORM_ID} onSubmit={handleSubmit} noValidate className="space-y-4">
        {submitted && <FormErrorSummary errors={errors} labels={labels} order={MILESTONE_FIELD_ORDER} />}
        <ServerError error={serverError} fallback={isEdit ? t('milestoneForm.saveFailed') : t('milestoneForm.createFailed')} />

        {pickProject && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label={t('milestoneForm.customer')} id="ms-customer" required error={errors['ms-customer']}>
              <EntityPicker
                icon={Building2}
                placeholder={t('milestoneForm.selectCustomer')}
                options={internalFirstPickerOptions(customers, internalOrgId, t(INTERNAL_WORK_LABEL_KEY), t(CUSTOMERS_GROUP_KEY))}
                value={values.customerId}
                onChange={(id) => { setValues((v) => ({ ...v, customerId: id, projectId: '', parentId: '', responsibleId: '', assigneeIds: [] })); }}
                onBlur={() => onBlurField('ms-customer')}
              />
            </Field>
            <Field
              label={t('milestoneForm.project')}
              id="ms-project"
              required
              error={errors['ms-project']}
              hint={values.customerId && !projectsLoading && projects.length === 0 ? t('milestoneForm.noProjects') : undefined}
            >
              <EntityPicker
                placeholder={values.customerId ? t('milestoneForm.selectProject') : t('milestoneForm.pickCustomerFirst')}
                options={projects.map((pr) => ({ value: pr.id, label: pr.name }))}
                value={values.projectId}
                disabled={!values.customerId}
                onChange={(id) => { setValues((v) => ({ ...v, projectId: id, parentId: '' })); }}
                onBlur={() => onBlurField('ms-project')}
              />
            </Field>
          </div>
        )}

        <Field label={t('milestoneForm.name')} id="ms-name" required error={errors['ms-name']} hint={coreName ? t('milestoneForm.coreNameHint') : undefined}>
          <input
            type="text"
            className="input"
            disabled={coreName}
            value={values.name}
            onChange={(e) => change('name', e.target.value, 'ms-name')}
            onBlur={() => onBlurField('ms-name')}
            placeholder={t('milestoneForm.namePlaceholder')}
            autoComplete="off"
          />
        </Field>

        <Field label={t('milestoneForm.description')} id="ms-description">
          <textarea className="input min-h-16" rows={2} value={values.description} onChange={(e) => set('description', e.target.value)} />
        </Field>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={t('milestoneForm.responsible')} id="ms-responsible" hint={t('milestoneForm.optional')}>
            <EntityPicker
              emptyLabel={t('milestoneForm.noResponsible')}
              options={agents.map((a) => ({ value: a.id, label: a.name }))}
              value={values.responsibleId}
              onChange={(id) => set('responsibleId', id)}
              disabled={!orgId}
              loading={agentsLoading}
            />
          </Field>
          <AssigneesPicker
            agents={agents}
            loading={agentsLoading}
            disabled={!orgId}
            selected={values.assigneeIds}
            onChange={(ids) => set('assigneeIds', ids)}
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={t('milestoneForm.startDate')} id="ms-start">
            <DateInput className="input" value={values.startDate} onChange={(e) => change('startDate', e.target.value, 'ms-due')} onBlur={() => onBlurField('ms-due')} />
          </Field>
          <Field label={t('milestoneForm.dueDate')} id="ms-due" error={errors['ms-due']}>
            <DateInput className="input" value={values.dueDate} onChange={(e) => change('dueDate', e.target.value, 'ms-due')} onBlur={() => onBlurField('ms-due')} />
          </Field>
        </div>

        <fieldset>
          <legend className="label">{t('milestoneForm.status')}</legend>
          <StatusCircles status={values.status} onChange={(s) => set('status', s)} label={t('milestoneForm.status')} />
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="label">{t('milestoneForm.plannedHours')}</legend>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {MILESTONE_PLANNED_MODES.map((m) => (
              <label key={m} className="inline-flex min-h-8 cursor-pointer items-center gap-1.5 text-sm text-text">
                <input type="radio" name="ms-planned-mode" value={m} checked={values.plannedMode === m} onChange={() => change('plannedMode', m, 'ms-planned')} />
                {t(`milestoneForm.plannedModes.${m}`)}
              </label>
            ))}
            {values.plannedMode === 'manual' && (
              <Field id="ms-planned" label={labels['ms-planned']} hideLabel required error={errors['ms-planned']} className="w-32">
                <input type="number" inputMode="decimal" min={0} step="0.5" className="input" value={values.plannedManual}
                  onChange={(e) => change('plannedManual', e.target.value, 'ms-planned')} onBlur={() => onBlurField('ms-planned')} />
              </Field>
            )}
          </div>
          {values.plannedMode === 'automatic' && <p className="text-xs text-text-2">{t('milestoneForm.plannedAutomaticHint')}</p>}
        </fieldset>

        <Field
          label={t('milestoneForm.parent')}
          id="ms-parent"
          hint={lockParent ? t(coreName ? 'milestoneForm.parentLockedCore' : 'milestoneForm.parentLockedHasSubs') : t('milestoneForm.parentHint')}
        >
          <EntityPicker
            emptyLabel={t('milestoneForm.topLevel')}
            options={parents.map((m) => ({ value: m.id, label: m.is_core ? `🔒 ${m.name}` : m.name }))}
            value={values.parentId}
            onChange={(id) => set('parentId', id)}
            disabled={lockParent || !projectId}
          />
        </Field>
        {isEdit && <HideFromCustomerToggle entity="milestone" id={self?.id} />}
      </form>
    </Dialog>
  );
}

/** "מצוותים": P-55c (D-230 sec.12) - the system's searchable multi-picker; only the chosen people show, as chips with ×. */
function AssigneesPicker({ agents, loading, disabled, selected, onChange }: {
  agents: { id: string; name: string; email?: string }[];
  loading: boolean;
  disabled?: boolean;
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  const { t } = useTranslation();
  return (
    <div id="ms-assignees" tabIndex={-1}>
      {disabled ? (
        <>
          <p className="label">{t('milestoneForm.assignees')}</p>
          <p className="text-sm text-text-2">{t('milestoneForm.pickProjectForAssignees')}</p>
        </>
      ) : loading ? (
        <>
          <p className="label">{t('milestoneForm.assignees')}</p>
          <p className="text-sm text-text-2">{t('taskAssignees.loading')}</p>
        </>
      ) : agents.length === 0 ? (
        <>
          <p className="label">{t('milestoneForm.assignees')}</p>
          <p className="text-sm text-text-2">{t('milestoneForm.noAgents')}</p>
        </>
      ) : (
        <AdditionalAssigneesCombobox
          agents={agents.map((a) => ({ ...a, name: a.name || a.email || a.id }))}
          ownerId=""
          selectedIds={selected}
          onChange={onChange}
          label={t('milestoneForm.assignees')}
        />
      )}
    </div>
  );
}
